/**
 * TenantService - Multi-tenant configuration detection and management
 * 
 * Detects the current tenant based on URL:
 * - Production: subdomain (e.g., scottbader.flexiva.co.uk → scott-bader)
 * - Development: query parameter (e.g., ?tenant=scott-bader)
 * 
 * Valid tenants are loaded from /oem/tenants.json at runtime.
 */

import { Logger } from './Logger';
import { fetchWithCacheBust } from '../utils/fetchUtils';

const logger = Logger.scope('TenantService');

export interface TenantInfo {
  id: string;
  basePath: string;
}

interface TenantsConfig {
  defaultTenant: string;
  tenants: string[];
}

class TenantServiceImpl {
  private tenantInfo: TenantInfo | null = null;
  private initialized = false;
  private initError: string | null = null;
  private validTenants: string[] = [];
  private defaultTenant: string = 'flexiva';

  /**
   * Initialize the tenant service by detecting the current tenant
   * Must be called before any other methods
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Load valid tenants from JSON config
    try {
      const response = await fetchWithCacheBust('/oem/tenants.json');
      if (response.ok) {
        const config: TenantsConfig = await response.json();
        this.validTenants = config.tenants || [];
        this.defaultTenant = config.defaultTenant || 'flexiva';
        logger.debug(`Loaded ${this.validTenants.length} tenants from config`);
      } else {
        // Fallback if config not found
        logger.warn('tenants.json not found, using fallback');
        this.validTenants = ['flexiva'];
        this.defaultTenant = 'flexiva';
      }
    } catch (error) {
      logger.error('Failed to load tenants.json:', error);
      this.validTenants = ['flexiva'];
      this.defaultTenant = 'flexiva';
    }

    const detectedTenant = this.detectTenant();
    
    // Validate tenant exists
    if (!this.isValidTenant(detectedTenant)) {
      this.initError = detectedTenant;
      this.initialized = true;
      logger.error(`Invalid tenant: ${detectedTenant}`);
      return;
    }

    this.tenantInfo = {
      id: detectedTenant,
      basePath: `/oem/${detectedTenant}`
    };

    this.initialized = true;
    logger.info(`Tenant initialized: ${detectedTenant}`);
  }

  /**
   * Check if initialization resulted in an error (invalid tenant)
   */
  public hasError(): boolean {
    return this.initError !== null;
  }

  /**
   * Get the error message (invalid tenant ID)
   */
  public getError(): string | null {
    return this.initError;
  }

  /**
   * Detect tenant from URL
   * Priority: query parameter > subdomain > default
   */
  private detectTenant(): string {
    // 1. Check query parameter (?tenant=xxx)
    const urlParams = new URLSearchParams(window.location.search);
    const queryTenant = urlParams.get('tenant');
    if (queryTenant) {
      logger.debug(`Tenant from query param: ${queryTenant}`);
      return queryTenant.toLowerCase();
    }

    // 2. Check domain-to-tenant mapping (custom domains)
    const hostname = window.location.hostname;
    
    // Domain-to-tenant mapping for standalone sites
    const domainTenantMap: Record<string, string> = {
      'cloud-sync.email': 'cloud-sync',
      'www.cloud-sync.email': 'cloud-sync',
      'api.business-software.group': 'bsg-api-hub',
    };
    
    const mappedTenant = domainTenantMap[hostname];
    if (mappedTenant) {
      logger.debug(`Tenant from domain mapping: ${mappedTenant}`);
      return mappedTenant;
    }
    
    // 3. Check subdomain (xxx.flexiva.co.uk)
    // Skip subdomain detection for localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      logger.debug('Localhost detected, using default tenant');
      return this.defaultTenant;
    }

    // Skip subdomain detection for Railway auto-generated domains
    if (hostname.endsWith('.up.railway.app')) {
      logger.debug('Railway domain detected, using default tenant');
      return this.defaultTenant;
    }

    // Extract subdomain from hostname
    // e.g., "scottbader.flexiva.co.uk" → "scottbader"
    // e.g., "app.flexiva.co.uk" → "app" (maps to default)
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const subdomain = parts[0].toLowerCase();
      
      // Map subdomain to tenant ID
      // "app" is the default Flexiva tenant
      if (subdomain === 'app') {
        logger.debug('Subdomain "app" detected, using default tenant');
        return this.defaultTenant;
      }
      
      // Convert subdomain format to tenant ID
      // e.g., "scottbader" → "scott-bader"
      const tenantId = this.subdomainToTenantId(subdomain);
      logger.debug(`Tenant from subdomain: ${tenantId}`);
      return tenantId;
    }

    // 3. Default tenant
    logger.debug('No tenant detected, using default');
    return this.defaultTenant;
  }

  /**
   * Convert subdomain format to tenant ID
   * e.g., "scottbader" → "scott-bader"
   */
  private subdomainToTenantId(subdomain: string): string {
    // Map of subdomain → tenant ID
    const subdomainMap: Record<string, string> = {
      'scottbader': 'scott-bader',
      'scott-bader': 'scott-bader',
      'trisys': 'trisys',
      'flexiva': 'flexiva',
      // TriSys tenant subdomains (trisys.co.uk domain)
      '2026': 'trisys-2026-01-13'
    };

    return subdomainMap[subdomain] || subdomain;
  }

  /**
   * Check if a tenant ID is valid
   */
  private isValidTenant(tenantId: string): boolean {
    return this.validTenants.includes(tenantId);
  }

  /**
   * Get the current tenant ID
   */
  public getTenantId(): string {
    if (!this.initialized) {
      throw new Error('TenantService not initialized. Call initialize() first.');
    }
    if (this.initError) {
      throw new Error(`Invalid tenant: ${this.initError}`);
    }
    return this.tenantInfo!.id;
  }

  /**
   * Get the base path for tenant-specific files
   * e.g., "/oem/flexiva"
   */
  public getBasePath(): string {
    if (!this.initialized) {
      throw new Error('TenantService not initialized. Call initialize() first.');
    }
    if (this.initError) {
      throw new Error(`Invalid tenant: ${this.initError}`);
    }
    return this.tenantInfo!.basePath;
  }

  /**
   * Get the path to a tenant-specific file
   * @param filename - The filename (e.g., "oem.json", "NavBar.json")
   */
  public getFilePath(filename: string): string {
    return `${this.getBasePath()}/${filename}`;
  }

  /**
   * Check if running in development mode (localhost)
   */
  public isDevelopment(): boolean {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  }

  /**
   * Get the default tenant ID
   */
  public getDefaultTenantId(): string {
    return this.defaultTenant;
  }

  /**
   * Get list of all valid tenant IDs
   */
  public getValidTenants(): readonly string[] {
    return this.validTenants;
  }
}

// Export singleton instance
export const tenantService = new TenantServiceImpl();
